import type {
  SessionDto,
  ProjectDto,
  OrganizationsDto,
} from './dto/session.dto.ts'
import { apiClient } from '../../shared/data/api-client.ts'
import authApiClient from '@/features/shared/data/axios.interceptor.ts'
import { apiAuthBaseUrl, managementBaseUrl } from '@/logic/configuration.ts'
import { WorkflowId } from '@/models'

export type PatchableSessionDto = Partial<SessionDto> & {
  projectId: string
  sessionId: string
}

export interface UserDto {
  id: string
  email: string
  firstName: string
  lastName: string
}

const SessionService = {
  getProjects: async (): Promise<ProjectDto[]> => {
    const response = await apiClient.get<ProjectDto[]>('/v3/project')
    return response.data
  },

  getProject: async (projectId: string): Promise<ProjectDto> => {
    const response = await apiClient.get<ProjectDto>(`/v3/project/${projectId}`)
    return response.data
  },

  createProject: async (projectName: string): Promise<ProjectDto> => {
    const body = { projectName }
    const response = await apiClient.post<ProjectDto>('/v3/project', body)
    return response.data
  },

  getSession: async (
    projectId: string,
    sessionId: string
  ): Promise<SessionDto> => {
    const response = await apiClient.get<SessionDto>(
      `/v3/project/${projectId}/session/${sessionId}`
    )
    return response.data
  },

  createSession: async (
    projectId: string,
    sessionName: string,
    audioformId?: string,
    state: Record<string, unknown> = {}
  ): Promise<SessionDto> => {
    const body = {
      sessionName,
      workflowId: WorkflowId.PRO_EDITOR,
      status: 'draft',
      state,
      audioformId,
      projectId,
    }
    const response = await apiClient.post<SessionDto>(
      `/v3/project/${projectId}/session`,
      body
    )
    return response.data
  },

  patchSession: async (session: PatchableSessionDto): Promise<SessionDto> => {
    const response = await apiClient.patch<SessionDto>(
      `/v3/project/${session.projectId}/session/${session.sessionId}`,
      session
    )
    return response.data
  },

  getOrganizationId: async (): Promise<string | undefined> => {
    const response = await authApiClient.get<OrganizationsDto>(
      `${managementBaseUrl}/org`
    )

    return response.data.organizations[0]?.orgId
  },

  getCurrentUser: async (): Promise<UserDto> => {
    const response = await authApiClient.get<UserDto>(
      `${apiAuthBaseUrl}/users/me`
    )
    return response.data
  },
}

export default SessionService
